// Ported from markup_parser/src/components/FlowVisualization.tsx.
// Filename kept as FlowCanvas to avoid churn in ArcPilotShell, but the
// implementation is the swimlane React Flow renderer driven by parseFlow().

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  useStore,
  type Edge,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { parseFlow } from '../utils/flowParser';
import type { NewModel, ParsedFlow, Statement } from '../utils/flowModel';
import FlowNavBar from './FlowNavBar';
import {
  COLUMN_GAP,
  COLUMN_WIDTH,
  CONTENT_START_X,
  LANE_LABEL_WIDTH,
  collectEventRefs,
  layoutFlow,
  type Lane,
  type LaidOutFlow,
} from '../utils/flowLayout';
import '../styles/FlowCanvas.css';

interface Props {
  newModel: NewModel;
  // External jump-to-statement request (e.g. from FeaturesPanel "show in flow").
  // FlowCanvas consumes it on the next render and calls onPendingFocusConsumed.
  pendingFocus?: number[] | null;
  onPendingFocusConsumed?: () => void;
  // External canvas-export request (palette command). Resolves once the
  // download has fired (or failed) so the caller can clear the pending
  // state and surface errors. Same consume-and-clear pattern as focus.
  pendingExport?: 'svg' | 'png' | null;
  onPendingExportConsumed?: (err?: Error) => void;
  // Shared bounded-context filter. Lives at the shell level so the
  // Features panel can pivot the same selection. Null = no filter.
  selectedBoundedContext?: string | null;
  onBoundedContextChange?: (bc: string | null) => void;
  // Optional "add to VSM" affordance. When provided, each statement
  // card renders a stopwatch button that opens the modal hosted in
  // ArcPilotShell with the clicked statement preselected. Omit to
  // hide the button (e.g. when there's no session-level handler yet).
  onAddToVsm?: (statementId: number) => void;
  // 083: an alternate flow-text parser (e.g. the step-flow grammar's
  // adapter). Omitted = the default discovery grammar parseFlow.
  parseFlowText?: (text: string) => ParsedFlow;
  // 083: column assignment mode — 'layered' follows the from-edges so
  // branches stack; omitted keeps the discovery grammar's layout.
  columnMode?: 'grammar' | 'layered';
  // A method may provide the richer shared process-spine navigation above
  // the canvas. Default true preserves every existing canvas composition.
  showNavigation?: boolean;
  // 087: streams the live viewport (pan/zoom) so a companion layer (the
  // Event Storming top card layer) can stay horizontally aligned with the
  // canvas columns. Fired on init and on every move.
  onViewportChange?: (viewport: { x: number; y: number; zoom: number }) => void;
  // 088: statements rendered as a ghost preview — proposed, not yet
  // confirmed. They lay out exactly where they will land and read dimmed
  // and dashed; they never count as "new" until they are confirmed.
  ghostIds?: Set<number>;
  // A staged document reader can name its scope and expose the live turn
  // boundary. Saved hydration is then baselined; only a new live turn may
  // focus IDs that arrive in a later document refresh.
  contentScopeKey?: string | null;
  contentRevision?: string;
  liveAdditionSignal?: string | number;
  liveAdditionActive?: boolean;
}

const CARD_WIDTH = COLUMN_WIDTH - 30;
const NO_GHOSTS = new Set<number>();

function laneSidebarDensity(zoom: number): 'compact' | 'normal' {
  return zoom < 0.45 ? 'compact' : 'normal';
}

const FlowCanvas: React.FC<Props> = ({
  newModel,
  pendingFocus,
  onPendingFocusConsumed,
  pendingExport,
  onPendingExportConsumed,
  selectedBoundedContext,
  onBoundedContextChange,
  onAddToVsm,
  parseFlowText,
  columnMode,
  showNavigation = true,
  onViewportChange,
  ghostIds = NO_GHOSTS,
  contentScopeKey,
  contentRevision,
  liveAdditionSignal,
  liveAdditionActive = false,
}) => {
  const flow = useMemo(
    () => (parseFlowText ?? parseFlow)(newModel.flowText || ''),
    [newModel.flowText, parseFlowText],
  );
  // If the shell hasn't lifted the filter state yet, manage it locally.
  // The shell can pass both props later; useState here is harmless then
  // because the controlled value takes precedence in the render path.
  const [localBc, setLocalBc] = useState<string | null>(null);
  const effectiveBc = selectedBoundedContext !== undefined ? selectedBoundedContext : localBc;
  const setEffectiveBc = onBoundedContextChange ?? setLocalBc;
  return (
    <FlowVisualizationInner
      flow={flow}
      nextQuestion={newModel.nextQuestion}
      frozenBoundary={newModel.frozenBoundary || 0}
      pendingFocus={pendingFocus}
      onPendingFocusConsumed={onPendingFocusConsumed}
      pendingExport={pendingExport}
      onPendingExportConsumed={onPendingExportConsumed}
      selectedBoundedContext={effectiveBc}
      onBoundedContextChange={setEffectiveBc}
      onAddToVsm={onAddToVsm}
      columnMode={columnMode}
      showNavigation={showNavigation}
      onViewportChange={onViewportChange}
      ghostIds={ghostIds}
      contentScopeKey={contentScopeKey}
      contentRevision={contentRevision}
      liveAdditionSignal={liveAdditionSignal}
      liveAdditionActive={liveAdditionActive}
    />
  );
};

export default FlowCanvas;

function FlowVisualizationInner({
  flow,
  nextQuestion,
  frozenBoundary,
  pendingFocus,
  onPendingFocusConsumed,
  pendingExport,
  onPendingExportConsumed,
  selectedBoundedContext,
  onBoundedContextChange,
  onAddToVsm,
  columnMode,
  showNavigation,
  onViewportChange,
  ghostIds,
  contentScopeKey,
  contentRevision,
  liveAdditionSignal,
  liveAdditionActive,
}: {
  flow: ParsedFlow;
  nextQuestion: string;
  frozenBoundary: number;
  pendingFocus?: number[] | null;
  onPendingFocusConsumed?: () => void;
  pendingExport?: 'svg' | 'png' | null;
  onPendingExportConsumed?: (err?: Error) => void;
  selectedBoundedContext: string | null;
  onBoundedContextChange: (bc: string | null) => void;
  onAddToVsm?: (statementId: number) => void;
  columnMode?: 'grammar' | 'layered';
  showNavigation: boolean;
  onViewportChange?: (viewport: { x: number; y: number; zoom: number }) => void;
  ghostIds: Set<number>;
  contentScopeKey?: string | null;
  contentRevision?: string;
  liveAdditionSignal?: string | number;
  liveAdditionActive: boolean;
}) {
  const [selectedError, setSelectedError] = useState<number | null>(null);
  const [activeStmtId, setActiveStmtId] = useState<number | null>(null);
  const laid = useMemo(
    () => layoutFlow(flow, { selectedBoundedContext, columnMode }),
    [flow, selectedBoundedContext, columnMode],
  );

  // Track which statement IDs are "newly added" since the last canvas
  // render so we can ring them and pan the viewport to them after the
  // model commits a turn.
  //
  // prevIdsRef starts at null so the FIRST effect run on a resumed
  // session takes a snapshot rather than highlighting every existing
  // statement as new. New (empty) sessions start with an empty Set
  // baseline, so the first turn's additions DO get highlighted.
  const prevIdsRef = useRef<Set<number> | null>(null);
  const contentScopeRef = useRef<string | null | undefined>(undefined);
  const contentRevisionRef = useRef<string | undefined>(undefined);
  const liveAdditionRef = useRef(false);
  const liveAdditionSignalRef = useRef<string | number | undefined>(undefined);
  const focusArmedRef = useRef(false);
  const pendingLiveSignalRef = useRef(false);
  const reactFlowRef = useRef<ReactFlowInstance | null>(null);
  const [freshIds, setFreshIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const currentIds = new Set(flow.statements.map((s) => s.id));

    // First effect: snapshot only, never highlight. Covers session resumes
    // where the canvas mounts with an already-populated Flow.
    const scopedHydration = contentScopeKey !== undefined;
    if (scopedHydration && contentScopeRef.current !== contentScopeKey) {
      contentScopeRef.current = contentScopeKey;
      contentRevisionRef.current = contentRevision;
      liveAdditionRef.current = liveAdditionActive;
      liveAdditionSignalRef.current = liveAdditionSignal;
      focusArmedRef.current = false;
      pendingLiveSignalRef.current = false;
      prevIdsRef.current = currentIds;
      setFreshIds(new Set());
      return;
    }
    if (prevIdsRef.current === null) {
      prevIdsRef.current = currentIds;
      contentRevisionRef.current = contentRevision;
      liveAdditionRef.current = liveAdditionActive;
      liveAdditionSignalRef.current = liveAdditionSignal;
      return;
    }

    const signalChanged = scopedHydration && liveAdditionSignalRef.current !== liveAdditionSignal;
    const revisionAdvanced = scopedHydration && contentRevisionRef.current !== contentRevision;
    if (signalChanged && !liveAdditionActive) pendingLiveSignalRef.current = true;
    if (revisionAdvanced && !liveAdditionActive && !liveAdditionRef.current) pendingLiveSignalRef.current = false;
    if (scopedHydration && liveAdditionActive && !liveAdditionRef.current && (signalChanged || pendingLiveSignalRef.current)) {
      focusArmedRef.current = true;
      pendingLiveSignalRef.current = false;
    }
    liveAdditionRef.current = liveAdditionActive;
    liveAdditionSignalRef.current = liveAdditionSignal;
    contentRevisionRef.current = contentRevision;
    const mayFocus = !scopedHydration || focusArmedRef.current;
    const fresh = mayFocus
      ? new Set(Array.from(currentIds).filter((id) => !prevIdsRef.current!.has(id)))
      : new Set<number>();
    prevIdsRef.current = currentIds;
    if (scopedHydration && (fresh.size > 0 || revisionAdvanced)) focusArmedRef.current = false;
    if (fresh.size === 0) return;

    setFreshIds(fresh);
  }, [flow.statements, contentScopeKey, contentRevision, liveAdditionSignal, liveAdditionActive]);

  useEffect(() => {
    if (freshIds.size === 0) return;
    // Pan/zoom the viewport so the new statements are in view. Slight
    // delay so React Flow has the freshly-built nodes laid out before
    // we ask it to fit them. maxZoom keeps a single new statement from
    // zooming uncomfortably close.
    const navTimer = setTimeout(() => {
      const inst = reactFlowRef.current;
      if (!inst) return;
      const targets = Array.from(freshIds).map((id) => ({ id: String(id) }));
      try {
        inst.fitView({ nodes: targets, padding: 0.3, duration: 600, maxZoom: 1.0 });
      } catch {
        /* fitView throws if a node id isn't yet in the graph — safe to ignore */
      }
    }, 200);

    // Fade the highlight after a few seconds so it doesn't linger.
    const fadeTimer = setTimeout(() => setFreshIds(new Set()), 6000);

    return () => {
      clearTimeout(navTimer);
      clearTimeout(fadeTimer);
    };
  }, [freshIds]);

  const { nodes, edges, totalWidth, totalHeight, errorsByStmt } = useMemo(
    () => buildGraph(flow, laid, freshIds, frozenBoundary, laid.dimmedStatementIds, onAddToVsm, ghostIds),
    [flow, laid, freshIds, frozenBoundary, onAddToVsm, ghostIds],
  );

  // Track the navbar's actual height (it grows when the BC filter is
  // active and the related strip appears) and write it as a CSS var so
  // the lane sidebar + react-flow viewport offsets stay aligned.
  const hostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const navEl = host.querySelector('.flow-nav-bar') as HTMLElement | null;
    if (!navEl) return;
    const apply = () => {
      const h = navEl.getBoundingClientRect().height;
      host.style.setProperty('--flow-nav-h', `${Math.ceil(h)}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(apply);
    ro.observe(navEl);
    return () => ro.disconnect();
  }, [selectedBoundedContext]);

  // Shared focus controller used by the nav-bar chips, FeaturesPanel
  // ("show in flow"), and the new-statement highlight effect above.
  const focusOnStatements = useCallback((ids: number[]) => {
    const inst = reactFlowRef.current;
    if (!inst || ids.length === 0) return;
    const targets = ids.map((id) => ({ id: String(id) }));
    try {
      inst.fitView({ nodes: targets, padding: 0.3, duration: 500, maxZoom: 1.2 });
      // Set active immediately so the nav-bar chip lights up before the
      // pan/zoom animation lands and onMoveEnd fires.
      if (ids.length === 1) setActiveStmtId(ids[0]);
    } catch {
      /* fitView throws if a node id isn't yet in the graph — safe to ignore */
    }
  }, []);

  // External pending-focus requests (FeaturesPanel "show in flow"). Consumed
  // on the next render so the parent doesn't need to coordinate retries.
  useEffect(() => {
    if (!pendingFocus || pendingFocus.length === 0) return;
    focusOnStatements(pendingFocus);
    onPendingFocusConsumed?.();
  }, [pendingFocus, focusOnStatements, onPendingFocusConsumed]);

  // External pending-export requests (palette command). Same consume-once
  // pattern: parent sets pendingExport, FlowCanvas runs it and calls back.
  // We wait for ReactFlow's onInit AND a short timeout so measured node
  // dimensions are populated before computeBounds reads them — onInit
  // fires before the first layout pass, so reading bounds immediately
  // gives undefined widths.
  const [reactFlowReady, setReactFlowReady] = useState(false);

  // When the BC filter toggles, the new layout is usually much smaller
  // than the unfiltered canvas — and the existing viewport (often panned
  // / zoomed away from origin) leaves the new content offscreen. Reset
  // by fitting all visible cards on every filter change.
  const prevBcRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevBcRef.current === undefined) {
      prevBcRef.current = selectedBoundedContext;
      return; // first render — let the default viewport stand
    }
    if (prevBcRef.current === selectedBoundedContext) return;
    prevBcRef.current = selectedBoundedContext;
    if (!reactFlowReady) return;
    const inst = reactFlowRef.current;
    if (!inst) return;
    const t = setTimeout(() => {
      try { inst.fitView({ padding: 0.15, duration: 400, maxZoom: 1.0 }); }
      catch { /* fitView throws if nodes aren't laid out yet */ }
    }, 60);
    return () => clearTimeout(t);
  }, [selectedBoundedContext, reactFlowReady]);

  useEffect(() => {
    if (!pendingExport) return;
    if (!reactFlowReady) return;
    const inst = reactFlowRef.current;
    if (!inst) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const { exportFlowCanvas } = await import('../utils/exportFlowCanvas');
        await exportFlowCanvas(inst, { format: pendingExport });
        if (!cancelled) onPendingExportConsumed?.();
      } catch (err) {
        if (!cancelled) onPendingExportConsumed?.(err instanceof Error ? err : new Error(String(err)));
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pendingExport, reactFlowReady, onPendingExportConsumed]);

  // Active-statement detection: after a pan/zoom settles, find the
  // statement-card node closest to the viewport center. Only fires on
  // onMoveEnd so we're not running this every animation frame.
  const recomputeActive = useCallback(() => {
    const inst = reactFlowRef.current;
    if (!inst) return;
    const { x: tx, y: ty, zoom } = inst.getViewport();
    const host = document.querySelector('.flow-host') as HTMLElement | null;
    const w = host?.clientWidth || 800;
    const h = host?.clientHeight || 600;
    // Convert viewport pixel center → graph coords.
    const cx = (-tx + w / 2) / zoom;
    const cy = (-ty + h / 2) / zoom;

    let bestId: number | null = null;
    let bestDist = Infinity;
    for (const s of flow.statements) {
      const node = inst.getNode(String(s.id));
      if (!node) continue;
      const nx = node.position.x + ((node.measured?.width ?? 200) / 2);
      const ny = node.position.y + ((node.measured?.height ?? 80) / 2);
      const d = Math.hypot(nx - cx, ny - cy);
      if (d < bestDist) { bestDist = d; bestId = s.id; }
    }
    if (bestId !== null) setActiveStmtId(bestId);
  }, [flow.statements]);

  const errorCount = flow.errors.filter((e) => e.severity === 'error').length;
  const warningCount = flow.errors.filter((e) => e.severity === 'warning').length;

  if (flow.statements.length === 0) {
    return (
      <div className="flow-empty">
        <div className="flow-empty-icon">◇</div>
        <div className="flow-empty-title">No flow yet</div>
        <div className="flow-empty-sub">
          {nextQuestion
            ? 'Answer the question to start building the flow.'
            : 'Statements will appear here as the session progresses.'}
        </div>
      </div>
    );
  }

  return (
    <div className={`flow-host${showNavigation ? '' : ' flow-host--without-navigation'}`} ref={hostRef}>
      {showNavigation ? (
        <FlowNavBar
          flow={flow}
          activeStmtId={activeStmtId}
          onJump={(id) => focusOnStatements([id])}
          selectedBoundedContext={selectedBoundedContext}
          onBoundedContextChange={onBoundedContextChange}
        />
      ) : null}
      {(errorCount > 0 || warningCount > 0) && (
        <div
          className={`flow-banner ${
            errorCount > 0 ? 'flow-banner-error' : 'flow-banner-warn'
          }`}
        >
          {errorCount > 0 && (
            <strong>
              {errorCount} error{errorCount > 1 ? 's' : ''}
            </strong>
          )}
          {errorCount > 0 && warningCount > 0 && ' · '}
          {warningCount > 0 && (
            <>
              {warningCount} warning{warningCount > 1 ? 's' : ''}
            </>
          )}
          <span className="flow-banner-hint"> in this flow</span>
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={(inst) => {
          reactFlowRef.current = inst;
          setReactFlowReady(true);
          // Seed active to the first node so the nav bar lights up
          // immediately rather than waiting for the user to pan.
          if (flow.statements.length > 0) {
            setActiveStmtId(flow.statements[0].id);
          }
          onViewportChange?.(inst.getViewport());
        }}
        onMove={(_event, viewport) => onViewportChange?.(viewport)}
        onMoveEnd={recomputeActive}
        defaultViewport={{ x: 0, y: 0, zoom: 0.9 }}
        minZoom={0.15}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        panOnDrag
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        translateExtent={[
          [-400, -60],
          [totalWidth + 400, totalHeight + 200],
        ]}
        onNodeClick={(_e, n) => {
          if (errorsByStmt.has(Number(n.id))) {
            setSelectedError(Number(n.id));
          } else {
            setSelectedError(null);
          }
        }}
      >
        <Background color="#e2e8f0" gap={20} />
        <Controls showInteractive={false} />
        <LaneSidebar lanes={laid.lanes} laid={laid} />
      </ReactFlow>
      {selectedError !== null && errorsByStmt.has(selectedError) && (
        <div className="error-popover">
          <div className="error-popover-head">
            <strong>Error on [{selectedError}]</strong>
            <button onClick={() => setSelectedError(null)}>×</button>
          </div>
          {errorsByStmt.get(selectedError)!.map((e, i) => (
            <div key={i} className="error-popover-item">
              <div className="error-popover-code">{e.code}</div>
              <div>{e.message}</div>
              {e.source && <pre>{e.source}</pre>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function buildGraph(
  flow: ParsedFlow,
  laid: LaidOutFlow,
  freshIds: Set<number> = new Set(),
  frozenBoundary = 0,
  dimmedStatementIds: Set<number> = new Set(),
  onAddToVsm?: (statementId: number) => void,
  ghostIds: Set<number> = NO_GHOSTS,
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const byId = new Map<number, Statement>();
  flow.statements.forEach((s) => byId.set(s.id, s));

  const errorsByStmt = new Map<number, typeof flow.errors>();
  for (const err of flow.errors) {
    if (err.statementId == null) continue;
    const arr = errorsByStmt.get(err.statementId) ?? [];
    arr.push(err);
    errorsByStmt.set(err.statementId, arr);
  }

  const totalWidth =
    CONTENT_START_X + Math.max(1, laid.columnCount) * (COLUMN_WIDTH + COLUMN_GAP);
  let totalHeight = 0;
  for (const lh of laid.laneHeights.values()) totalHeight += lh;

  laid.lanes.forEach((lane, i) => {
    const top = laid.laneTops.get(lane.key) ?? 0;
    const height = laid.laneHeights.get(lane.key) ?? 150;
    nodes.push({
      id: `lane-${lane.key}`,
      type: 'lane',
      position: { x: 0, y: top },
      data: { lane, width: totalWidth, height, index: i },
      selectable: false,
      draggable: false,
      focusable: false,
      zIndex: 0,
    });
  });

  const columnX = (c: number) =>
    CONTENT_START_X + c * (COLUMN_WIDTH + COLUMN_GAP) + COLUMN_GAP / 2;

  // Frozen-boundary guide. The server's `frozenBoundary` is a statement [id] —
  // everything ≤ it is locked from delta edits. Translate that to an x position
  // by finding the rightmost column among locked statements and rendering a
  // vertical line in the gap to the right of that column.
  if (frozenBoundary > 0) {
    let maxFrozenCol = -1;
    for (const [id, col] of laid.columns) {
      if (id <= frozenBoundary && col > maxFrozenCol) maxFrozenCol = col;
    }
    if (maxFrozenCol >= 0 && maxFrozenCol < laid.columnCount) {
      // Position the guide in the middle of the gap after the frozen column.
      const guideX = CONTENT_START_X + (maxFrozenCol + 1) * (COLUMN_WIDTH + COLUMN_GAP) - COLUMN_GAP / 2;
      nodes.push({
        id: 'frozen-guide',
        type: 'frozenGuide',
        position: { x: guideX - 12, y: 0 },
        data: { height: totalHeight, boundary: frozenBoundary },
        selectable: false,
        draggable: false,
        focusable: false,
        zIndex: 1,
      });
    }
  }

  // Reactions stacked vertically in the same (lane, col) cell — offset downward.
  const stackCounter = new Map<string, number>();

  for (const s of flow.statements) {
    if (s.type === 'event') continue;
    const p = laid.placements.get(s.id);
    if (!p) continue;
    const laneTop = laid.laneTops.get(p.laneKey) ?? 0;
    const cardH = laid.cardHeights.get(s.id) ?? 88;

    const stackKey = `${p.laneKey}|${p.column}`;
    const stackIdx = stackCounter.get(stackKey) ?? 0;
    stackCounter.set(stackKey, stackIdx + 1);
    const yOffset = stackIdx * (cardH + 12);

    const x = columnX(p.column) + (COLUMN_WIDTH - CARD_WIDTH) / 2;
    const y = laneTop + 18 + yOffset;

    const childEvents = s.emits
      .filter((eid) => laid.embeddedEventIds.has(eid))
      .map((eid) => byId.get(eid))
      .filter((e): e is Statement => !!e);

    nodes.push({
      id: String(s.id),
      type: 'statement',
      position: { x, y },
      data: {
        statement: s,
        childEvents,
        hasError: errorsByStmt.has(s.id),
        height: cardH,
        isNew: freshIds.has(s.id) && !ghostIds.has(s.id),
        isGhost: ghostIds.has(s.id),
        onAddToVsm,
      },
      zIndex: 2,
      ...(dimmedStatementIds.has(s.id) ? { style: { opacity: 0.35 } } : {}),
    });
  }

  // Website-lane items (sends | state | receives) stacked per cell
  type Cell = {
    sends?: (typeof laid.websiteMarkers)[number];
    state?: (typeof laid.uiStateCards)[number];
    receives?: (typeof laid.websiteMarkers)[number];
  };
  const webCells = new Map<string, Cell>();
  const cellKey = (lane: string, c: number) => `${lane}|${c}`;
  for (const m of laid.websiteMarkers) {
    const k = cellKey(m.laneKey, m.column);
    const entry = webCells.get(k) ?? {};
    if (m.kind === 'sends') entry.sends = m;
    else entry.receives = m;
    webCells.set(k, entry);
  }
  for (const u of laid.uiStateCards) {
    const k = cellKey(u.laneKey, u.column);
    const entry = webCells.get(k) ?? {};
    entry.state = u;
    webCells.set(k, entry);
  }

  const SENDS_H = 28;
  const STATE_H = 66;
  const RECV_H = 28;
  const GAP = 6;

  for (const [k, cell] of webCells) {
    const [laneKey, colStr] = k.split('|');
    const column = parseInt(colStr, 10);
    const laneTop = laid.laneTops.get(laneKey) ?? 0;
    const baseX = columnX(column) + (COLUMN_WIDTH - CARD_WIDTH) / 2;
    let y = laneTop + 14;

    // Suffix every node/edge ID with the laneKey so an action that spans
    // multiple touchpoints (e.g. Website + MobileApp) renders a full
    // sends/state/receives stack on each lane instead of colliding into one.
    const laneSuffix = laneKey;

    if (cell.sends) {
      const action = byId.get(cell.sends.actionId);
      const dimmed = dimmedStatementIds.has(cell.sends.actionId);
      nodes.push({
        id: `mark-sends-${cell.sends.actionId}-${laneSuffix}`,
        type: 'webmark',
        position: { x: baseX + 10, y },
        data: {
          kind: 'sends',
          actionId: cell.sends.actionId,
          actionName: action?.name,
        },
        zIndex: 2,
        ...(dimmed ? { style: { opacity: 0.35 } } : {}),
      });
      y += SENDS_H + GAP;
    }

    if (cell.state) {
      const uiNodeId = `ui-${cell.state.event.id}-${laneSuffix}`;
      const dimmed = dimmedStatementIds.has(cell.state.emitterId);
      nodes.push({
        id: uiNodeId,
        type: 'uistate',
        position: { x: baseX, y },
        data: { event: cell.state.event, emitterId: cell.state.emitterId },
        zIndex: 2,
        ...(dimmed ? { style: { opacity: 0.35 } } : {}),
      });
      edges.push({
        id: `emit-ui-${cell.state.event.id}-${laneSuffix}`,
        source: String(cell.state.emitterId),
        target: uiNodeId,
        type: 'smoothstep',
        style: { stroke: '#2563eb', strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#2563eb' },
      });
      y += STATE_H + GAP;
    }

    if (cell.receives) {
      const ev = cell.receives.eventId ? byId.get(cell.receives.eventId) : undefined;
      const dimmed = dimmedStatementIds.has(cell.receives.actionId);
      nodes.push({
        id: `mark-receives-${cell.receives.actionId}-${cell.receives.eventId}-${laneSuffix}`,
        type: 'webmark',
        position: { x: baseX + 10, y },
        data: {
          kind: 'receives',
          actionId: cell.receives.actionId,
          eventId: cell.receives.eventId,
          eventName: ev?.name,
        },
        zIndex: 2,
        ...(dimmed ? { style: { opacity: 0.35 } } : {}),
      });
      y += RECV_H + GAP;
    }
  }

  // Edges: :: from (action → handler)
  for (const s of flow.statements) {
    if (s.triggeredBy?.kind !== 'from') continue;
    const src = s.triggeredBy.actionId;
    // Use placements to skip edges whose endpoints were filtered out
    // by the BC selector — without this they'd dangle to nowhere.
    if (!laid.placements.has(src) || !laid.placements.has(s.id)) continue;
    const dim = dimmedStatementIds.has(src) || dimmedStatementIds.has(s.id);
    const targetHandle = s.type === 'reaction' ? 'in-left' : 'in-top';
    edges.push({
      id: `from-${src}-${s.id}`,
      source: String(src),
      target: String(s.id),
      targetHandle,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#94a3b8', strokeWidth: 1.25, strokeDasharray: '5 4', ...(dim ? { opacity: 0.35 } : {}) },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
    });
  }

  // Edges: :: on (event → reaction/command/query). Source is the emitter card,
  // anchored on the row for the event handle. #ui events route from the ui state card.
  for (const s of flow.statements) {
    if (s.triggeredBy?.kind !== 'on') continue;
    const refs: number[] = [];
    collectEventRefs(s.triggeredBy.expr, refs);
    for (const r of refs) {
      const producerId = laid.producerOf.get(r);
      if (producerId === undefined) continue;
      // Skip edges whose producer was filtered out — would dangle.
      if (!laid.placements.has(producerId)) continue;
      const targetRendered =
        laid.placements.has(s.id) && !laid.hiddenEventIds.has(s.id);
      if (!targetRendered) continue;
      const hiddenLaneKey = laid.hiddenEventIds.has(r) ? laid.placements.get(r)?.laneKey : undefined;
      const sourceNodeId = hiddenLaneKey
        ? `ui-${r}-${hiddenLaneKey}`
        : String(producerId);
      const sourceHandle = laid.hiddenEventIds.has(r) ? undefined : `event-${r}`;
      const dim = dimmedStatementIds.has(producerId) || dimmedStatementIds.has(s.id);
      const targetHandle = s.type === 'reaction' ? 'in-left' : 'in-top';
      edges.push({
        id: `on-${r}-${s.id}`,
        source: sourceNodeId,
        sourceHandle,
        target: String(s.id),
        targetHandle,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#94a3b8', strokeWidth: 1.25, strokeDasharray: '5 4', ...(dim ? { opacity: 0.35 } : {}) },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
      });
    }
  }

  return { nodes, edges, totalWidth, totalHeight, errorsByStmt };
}

// ─── Node components ──────────────────────────────────────────────

function LaneBackgroundNode({ data }: NodeProps) {
  const lane = data.lane as Lane;
  return (
    <div
      className={`lane-bg lane-bg-${lane.kind} ${
        (data.index as number) % 2 === 0 ? 'lane-bg-even' : 'lane-bg-odd'
      }`}
      style={{ width: data.width as number, height: data.height as number }}
    />
  );
}

function StatementBlockNode({ data }: NodeProps) {
  const st = data.statement as Statement;
  const childEvents = (data.childEvents as Statement[] | undefined) ?? [];
  const hasError = !!data.hasError;
  const isNew = !!data.isNew;
  const isGhost = !!data.isGhost;
  const onAddToVsm = data.onAddToVsm as ((statementId: number) => void) | undefined;

  const isPolicy = st.tags?.policy === true || st.tags?.policy === 'true';
  const tagEntries = Object.entries(st.tags ?? {});
  const ownerTag = tagEntries.find(([k]) => k === 'owner')?.[1];
  const otherTags = tagEntries.filter(
    ([k]) => k !== 'owner' && k !== 'policy' && k !== 'ui',
  );

  return (
    <div
      className={`card card-${st.type}${hasError ? ' card-error' : ''}${isNew ? ' card-new' : ''}${isGhost ? ' card-ghost' : ''}`}
      style={{ width: CARD_WIDTH, minHeight: data.height as number }}
    >
      <Handle id="in-top" type="target" position={Position.Top} style={handleStyle} />
      <Handle id="in-left" type="target" position={Position.Left} style={handleStyle} />
      <div className="card-head">
        <span className="card-type">{st.type}</span>
        <span className="card-id">[{st.id}]</span>
        {onAddToVsm && (
          <button
            type="button"
            className="card-vsm-btn"
            title="Add to a Value Stream Map"
            onClick={(e) => {
              e.stopPropagation();
              onAddToVsm(st.id);
            }}
          >
            ⏱
          </button>
        )}
      </div>
      {isPolicy && (st.type === 'command' || st.type === 'reaction') && (
        <div className="card-policy">policy</div>
      )}
      <div className="card-name" title={st.name}>
        {st.name || '(unnamed)'}
      </div>
      <div className="card-meta">
        {st.target && <span className="card-target">&amp;{st.target}</span>}
        {st.via && st.via.sys.length > 0 && (
          <span className="card-via">
            {st.via.sys.map((s) => `%sys ${s}`).join(' ')}
          </span>
        )}
      </div>
      {childEvents.length > 0 && (
        <div className="card-children">
          {childEvents.map((ev) => {
            const isIntegration = ev.tags?.integration === true;
            const isAudit = ev.tags?.audit === true;
            const evOwner = ev.tags?.owner;
            return (
              <div key={ev.id} className="card-child-row">
                <Handle
                  id={`event-${ev.id}`}
                  type="source"
                  position={Position.Right}
                  style={{ ...handleStyle, top: 'auto' }}
                />
                <span className="card-child-id">[{ev.id}]</span>
                <span className="card-child-name" title={ev.name}>
                  {ev.name}
                </span>
                <span className="card-child-indicators">
                  {isIntegration && (
                    <span title="published / integration" className="ind ind-int">
                      ↗
                    </span>
                  )}
                  {isAudit && (
                    <span title="audit" className="ind ind-aud">
                      📖
                    </span>
                  )}
                  {evOwner && evOwner !== true && (
                    <span className="owner-chip" title={`owner: ${evOwner}`}>
                      {String(evOwner)}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {(ownerTag || otherTags.length > 0) && (
        <div className="card-tags">
          {ownerTag && ownerTag !== true && (
            <span className="owner-chip">owner: {String(ownerTag)}</span>
          )}
          {otherTags.map(([k, v]) => (
            <span key={k} className="tag-chip">
              #{k}
              {v !== true ? `=${v}` : ''}
            </span>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

function UiStateNode({ data }: NodeProps) {
  const ev = data.event as Statement;
  return (
    <div className="ui-state-card" style={{ width: CARD_WIDTH }}>
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div className="ui-state-head">
        <span>state</span>
        <span className="card-id">[{ev.id}]</span>
      </div>
      <div className="ui-state-name" title={ev.name}>
        {ev.name}
      </div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

function FrozenGuideNode({ data }: NodeProps) {
  const height = (data.height as number) || 0;
  const boundary = (data.boundary as number) || 0;
  return (
    <div className="frozen-guide" style={{ height }} aria-hidden>
      <div className="frozen-guide-line" />
      <div className="frozen-guide-label" title={`Statements [1]–[${boundary}] are locked from edits`}>
        <span className="frozen-guide-lock">🔒</span>
        <span className="frozen-guide-text">locked through [{boundary}]</span>
      </div>
    </div>
  );
}

function WebMarkNode({ data }: NodeProps) {
  if (data.kind === 'sends') {
    return (
      <div className="web-mark web-mark-sends" title="Data sent from the UI to the system">
        <span className="web-mark-arrow">→</span>
        <span className="web-mark-label">sends</span>
        {data.actionName ? (
          <span className="web-mark-target">{String(data.actionName)}</span>
        ) : null}
      </div>
    );
  }
  return (
    <div
      className="web-mark web-mark-receives"
      title="Confirmation event returned to the UI"
    >
      <span className="web-mark-arrow">←</span>
      <span className="web-mark-label">receives</span>
      {data.eventName ? (
        <span className="web-mark-target">
          {String(data.eventName)} <span className="web-mark-id">[{String(data.eventId)}]</span>
        </span>
      ) : null}
    </div>
  );
}

const nodeTypes = {
  lane: LaneBackgroundNode,
  statement: StatementBlockNode,
  uistate: UiStateNode,
  webmark: WebMarkNode,
  frozenGuide: FrozenGuideNode,
};

const handleStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  background: '#ffffff',
  border: '1.5px solid #94a3b8',
  opacity: 0,
};

// ─── Sticky lane sidebar ──────────────────────────────────────────

// Three logical sections, each grouping one or more lane kinds. The current
// layout interleaves website lanes under their owning actor, so a section
// label can appear more than once when actors are interleaved with their
// touchpoints — that's accurate to the actual lane order rather than
// pretending a non-contiguous group exists.
type LaneSection = '1. ACTORS' | '2. TOUCHPOINTS & APPS' | '3. BOUNDED CONTEXTS';
function sectionForKind(kind: Lane['kind']): LaneSection {
  if (kind === 'website') return '2. TOUCHPOINTS & APPS';
  if (kind === 'context') return '3. BOUNDED CONTEXTS';
  return '1. ACTORS';
}

function LaneSidebar({ lanes, laid }: { lanes: Lane[]; laid: LaidOutFlow }) {
  const transform = useStore((s) => s.transform);
  const [, ty, zoom] = transform;

  // Walk the lane order and remember the first lane of each contiguous
  // run of same-section lanes. Those positions get a section-cap chip
  // rendered above the lane label.
  const sectionCaps: { laneKey: string; label: LaneSection }[] = [];
  let prevSection: LaneSection | null = null;
  for (const lane of lanes) {
    const sec = sectionForKind(lane.kind);
    if (sec !== prevSection) {
      sectionCaps.push({ laneKey: lane.key, label: sec });
      prevSection = sec;
    }
  }
  const capByLaneKey = new Map(sectionCaps.map((c) => [c.laneKey, c.label]));

  return (
    <div className={`lane-sidebar lane-sidebar--${laneSidebarDensity(zoom)}`} style={{ width: LANE_LABEL_WIDTH }}>
      {lanes.map((lane) => {
        const top = ty + (laid.laneTops.get(lane.key) ?? 0) * zoom;
        const height = (laid.laneHeights.get(lane.key) ?? 150) * zoom;
        const cap = capByLaneKey.get(lane.key);
        return (
          <div
            key={lane.key}
            className={`lane-label lane-label-${lane.kind}`}
            style={{ top, height }}
          >
            {cap && <div className="lane-section-cap">{cap}</div>}
            <span className="lane-icon" aria-hidden>
              {iconFor(lane.kind)}
            </span>
            <div className="lane-text">
              <div className="lane-name" title={lane.label}>
                {lane.label}
              </div>
              <div className="lane-kind">{kindLabel(lane.kind)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function iconFor(kind: Lane['kind']): string {
  if (kind === 'human') return '👤';
  if (kind === 'externalSystem') return '🔌';
  if (kind === 'website') return '🖥';
  return '◆';
}

function kindLabel(kind: Lane['kind']): string {
  if (kind === 'human') return 'human';
  if (kind === 'context') return 'bounded context';
  if (kind === 'externalSystem') return 'external system';
  return 'website / UI';
}
